import { 
  X, 
  Workflow, 
  Plus, 
  Trash2, 
  Settings2,
  Activity, 
  Zap, 
  Database,
  Search,
  AlertCircle
} from "lucide-react"
import type { 
  LayerExperiencePipelineDefinition, 
  LayerExperiencePipelineStepDefinition,
  LayerExperienceEffectDefinition,
  LayerExperienceResolverAvailability,
  LayerExperienceResolverDefinition,
  LayerExperienceResolverInputBindingKind,
} from "../../types/layer-experience"
import type { MapPipelineDefinitionEditorProps } from "../../contracts/map-interactivity"
import { Label } from "../../internal/primitives/label"
import { Input } from "../../internal/primitives/input"
import { Separator } from "../../internal/primitives/separator"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select"
import { cn } from "../../lib/utils"

const resolveEffectTemplate = (effect: LayerExperienceEffectDefinition) => {
  if (typeof effect.input === "string") {
    return effect.input
  }
  if (
    effect.input &&
    typeof effect.input === "object" &&
    !Array.isArray(effect.input) &&
    typeof (effect.input as { template?: unknown }).template === "string"
  ) {
    return (effect.input as { template: string }).template
  }
  return ""
}

const resolveEffectField = (effect: LayerExperienceEffectDefinition) => {
  if (typeof effect.input === "string") {
    return effect.input
  }
  if (
    effect.input &&
    typeof effect.input === "object" &&
    !Array.isArray(effect.input) &&
    typeof (effect.input as { field?: unknown }).field === "string"
  ) {
    return (effect.input as { field: string }).field
  }
  return ""
}

const resolveResolverOptionValue = (resolver: LayerExperienceResolverDefinition) =>
  resolver.resolver_attachment_id || resolver.resolver_id

const resolveStepResolverValue = (step: Extract<LayerExperiencePipelineStepDefinition, { kind: "resolve" }>) =>
  step.resolver_attachment_id || step.resolver_id

const resolveResolverTitle = (resolver: LayerExperienceResolverDefinition) =>
  resolver.label || resolver.resolver_type_id || resolver.resolver_id

const resolveResolverAvailabilityLabel = (availability?: LayerExperienceResolverAvailability | null) => {
  switch (availability) {
    case "available":
      return "Available"
    case "tenant_not_entitled":
      return "Entitlement"
    case "connection_missing":
    case "connection_not_ready":
      return "Connection"
    case "user_forbidden":
      return "Restricted"
    case "layer_incompatible":
      return "Layer Mismatch"
    case "platform_disabled":
      return "Disabled"
    default:
      return "Managed"
  }
}

const resolveResolverAvailabilityClassName = (availability?: LayerExperienceResolverAvailability | null) => {
  switch (availability) {
    case "available":
      return "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
    case "tenant_not_entitled":
      return "bg-amber-500/10 text-amber-700 border-amber-500/20"
    case "connection_missing":
    case "connection_not_ready":
      return "bg-orange-500/10 text-orange-700 border-orange-500/20"
    case "user_forbidden":
      return "bg-rose-500/10 text-rose-700 border-rose-500/20"
    case "layer_incompatible":
      return "bg-slate-500/10 text-slate-700 border-slate-500/20"
    case "platform_disabled":
      return "bg-muted text-muted-foreground border-border/70"
    default:
      return "bg-muted/40 text-muted-foreground border-border/70"
  }
}

const resolveResolverDisabledReason = (resolver: LayerExperienceResolverDefinition) => {
  if (resolver.availability && resolver.availability !== "available") {
    return resolver.availability_reason || resolveResolverAvailabilityLabel(resolver.availability)
  }
  return null
}

const resolvePreferredResolver = (resolvers: LayerExperienceResolverDefinition[]) =>
  resolvers.find(resolver => !resolveResolverDisabledReason(resolver)) ?? resolvers[0] ?? null

const findResolverBySelection = (resolvers: LayerExperienceResolverDefinition[], selection: string) =>
  resolvers.find(resolver => resolveResolverOptionValue(resolver) === selection || resolver.resolver_id === selection) ?? null

const resolveFieldSemanticRoleOptions = (availableFields: MapPipelineDefinitionEditorProps["availableFields"]) => {
  const roleMap = new Map<string, { role: string; label: string; fieldId: string }>()
  for (const field of availableFields) {
    for (const role of field.semanticRoles ?? []) {
      if (!roleMap.has(role)) {
        roleMap.set(role, {
          role,
          label: role
            .split(/[_-]+/)
            .map(token => token.charAt(0).toUpperCase() + token.slice(1))
            .join(" "),
          fieldId: field.id,
        })
      }
    }
  }
  return Array.from(roleMap.values())
}

const buildDefaultInputMappings = (
  resolver: LayerExperienceResolverDefinition | null,
  availableFields: MapPipelineDefinitionEditorProps["availableFields"],
  builtinValues: MapPipelineDefinitionEditorProps["builtinValues"],
) => {
  const semanticRoleOptions = resolveFieldSemanticRoleOptions(availableFields)
  return (
    resolver?.required_inputs?.map((input, index) => {
      const normalizedInputKey = input.input_key.trim().toLowerCase()
      const exactField = availableFields.find(field => field.id.toLowerCase() === normalizedInputKey) ?? availableFields[index] ?? null
      const exactRole = semanticRoleOptions.find(option => option.role.toLowerCase() === normalizedInputKey) ?? null
      const bindingKind = input.binding_kind ?? (exactRole ? "semantic_role" : "feature_field")
      if (bindingKind === "semantic_role" && exactRole) {
        return {
          input_key: input.input_key,
          binding_kind: "semantic_role" as LayerExperienceResolverInputBindingKind,
          binding: {
            role: exactRole.role,
            field: exactRole.fieldId,
          },
          required: input.required ?? true,
        }
      }
      if (bindingKind === "builtin_value" && builtinValues[0]) {
        return {
          input_key: input.input_key,
          binding_kind: "builtin_value" as LayerExperienceResolverInputBindingKind,
          binding: {
            value_id: builtinValues[0].value_id,
          },
          required: input.required ?? true,
        }
      }
      return {
        input_key: input.input_key,
        binding_kind: bindingKind,
        binding:
          bindingKind === "constant"
            ? { value: "" }
            : bindingKind === "resolver_result"
              ? { step_id: "" }
              : { field: exactField?.id ?? availableFields[0]?.id ?? "" },
        required: input.required ?? true,
      }
    }) ?? []
  )
}

export function MapPipelineDefinitionEditor({
  mode,
  value,
  availableFields,
  builtinValues,
  resolvers,
  surfaces,
  onChange,
  onSave,
  onCancel,
  className
}: MapPipelineDefinitionEditorProps) {
  const invokableSurfaces = surfaces.filter(surface => surface.surface_kind !== "ambiguous_selection_summary")
  const preferredResolver = resolvePreferredResolver(resolvers)
  const semanticRoleOptions = resolveFieldSemanticRoleOptions(availableFields)

  const handleUpdate = (patch: Partial<LayerExperiencePipelineDefinition>) => {
    onChange({ ...value, ...patch })
  }

  const handleAddStep = () => {
    const nextStep: LayerExperiencePipelineStepDefinition = {
      step_id: `step-${Date.now()}`,
      kind: "resolve",
      resolver_id: preferredResolver?.resolver_id || "default",
      resolver_attachment_id: preferredResolver?.resolver_attachment_id || null,
      resolver_type_id: preferredResolver?.resolver_type_id || preferredResolver?.resolver_id || null,
      input_mappings: buildDefaultInputMappings(preferredResolver, availableFields, builtinValues),
      failure_policy: "continue_with_empty_result"
    }
    handleUpdate({ steps: [...value.steps, nextStep] })
  }

  const handleRemoveStep = (index: number) => {
    handleUpdate({ steps: value.steps.filter((_, i) => i !== index) })
  }

  const handleUpdateResolveStep = (
    index: number,
    updater: (
      step: Extract<LayerExperiencePipelineStepDefinition, { kind: "resolve" }>,
    ) => Extract<LayerExperiencePipelineStepDefinition, { kind: "resolve" }>,
  ) => {
    const current = value.steps[index]
    if (!current || current.kind !== "resolve") {
      return
    }
    const steps = [...value.steps]
    steps[index] = updater(current)
    handleUpdate({ steps })
  }

  const handleAddEffect = () => {
    const nextEffect: LayerExperienceEffectDefinition = {
      effect_id: `effect-${Date.now()}`,
      kind: "open_surface",
      surface_id: invokableSurfaces[0]?.surface_id || null
    }
    handleUpdate({ effects: [...value.effects, nextEffect] })
  }

  const handleRemoveEffect = (index: number) => {
    handleUpdate({ effects: value.effects.filter((_, i) => i !== index) })
  }

  return (
    <div className={cn("flex flex-col h-full bg-background border rounded-lg overflow-hidden shadow-xl", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-primary/10 text-primary">
            <Workflow className="w-4 h-4" />
          </div>
          <div className="flex flex-col">
            <h3 className="text-sm font-bold uppercase tracking-tight">
              {mode === "create" ? "New Pipeline" : "Edit Pipeline"}
            </h3>
            <p className="text-[10px] text-muted-foreground font-medium">Orchestrate multi-step logic and UI effects</p>
          </div>
        </div>
        <button onClick={onCancel} className="p-1 hover:bg-muted rounded-full transition-colors text-muted-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Core Metadata & Trigger */}
        <div className="grid gap-4">
           <div className="grid gap-1.5">
            <Label className="text-[10px] uppercase font-bold text-muted-foreground/70">Pipeline ID</Label>
            <Input 
              value={value.pipeline_id} 
              onChange={(e) => handleUpdate({ pipeline_id: e.target.value })}
              placeholder="e.g. fetch_and_show_details"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[10px] uppercase font-bold text-muted-foreground/70">Display Label</Label>
            <Input 
              value={value.label || ""} 
              onChange={(e) => handleUpdate({ label: e.target.value })}
              placeholder="e.g. Fetch Details & Open Panel"
              className="h-8 text-xs"
            />
          </div>

          <div className="p-3 border rounded-lg bg-primary/5 border-primary/10">
            <div className="flex items-center gap-2 mb-2">
               <Zap className="w-3.5 h-3.5 text-primary" />
               <Label className="text-[10px] uppercase font-bold text-primary/80">Trigger Context</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
               <div className="grid gap-1">
                  <Label className="text-[9px] uppercase text-muted-foreground/60">Event Type</Label>
                  <Select 
                    value={value.trigger.kind} 
                    onValueChange={(val: any) => handleUpdate({ trigger: { ...value.trigger, kind: val } })}
                  >
                    <SelectTrigger className="h-7 text-[10px] bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Launcher</SelectItem>
                      <SelectItem value="click">Click</SelectItem>
                      <SelectItem value="hover_enter">Hover Enter</SelectItem>
                      <SelectItem value="context_menu">Context Menu</SelectItem>
                      <SelectItem value="selection_changed">Selection Changed</SelectItem>
                      <SelectItem value="map_loaded">Map Loaded</SelectItem>
                    </SelectContent>
                  </Select>
               </div>
               <div className="grid gap-1">
                  <Label className="text-[9px] uppercase text-muted-foreground/60">Source Ref</Label>
                  <Select 
                    value={value.trigger.source || "feature"} 
                    onValueChange={(val) =>
                      handleUpdate({
                        trigger: {
                          ...value.trigger,
                          source: val as "manual" | "feature" | "global" | "viewport",
                        },
                      })
                    }
                  >
                    <SelectTrigger className="h-7 text-[10px] bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="feature">Target Feature</SelectItem>
                      <SelectItem value="global">Global Context</SelectItem>
                      <SelectItem value="viewport">Viewport Bounds</SelectItem>
                    </SelectContent>
                  </Select>
               </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Steps */}
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <Label className="text-[11px] font-bold uppercase text-muted-foreground tracking-tight">Logic Steps</Label>
              <p className="text-[10px] text-muted-foreground">Data resolvers and condition branching</p>
            </div>
            <button 
              onClick={handleAddStep}
              className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline"
            >
              <Plus className="w-3 h-3" /> Add Step
            </button>
          </div>

          <div className="space-y-4">
             {value.steps.map((step, idx) => (
               <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center pt-2">
                     <div className="w-6 h-6 rounded-full bg-muted border flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                     </div>
                     {idx < value.steps.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
                  </div>
                  <div className="flex-1 p-3 border rounded-lg bg-card relative group">
                     <button 
                       onClick={() => handleRemoveStep(idx)}
                       className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground/40 hover:text-destructive"
                     >
                        <Trash2 className="w-3 h-3" />
                     </button>

                     <div className="grid gap-3">
                        <div className="flex items-center gap-2">
                           <StepKindIcon kind={step.kind} className="w-3.5 h-3.5 text-muted-foreground" />
                           <span className="text-[10px] font-bold uppercase text-muted-foreground">{step.kind}</span>
                        </div>

                        {step.kind === "resolve" && (
                           <div className="grid gap-3">
                              <div className="grid gap-1">
                                 <Label className="text-[9px] uppercase text-muted-foreground/60">Resolver</Label>
                                 <Select 
                                   value={resolveStepResolverValue(step)} 
                                   onValueChange={(val) => {
                                      const selectedResolver = findResolverBySelection(resolvers, val)
                                      handleUpdateResolveStep(idx, current => ({
                                        ...current,
                                        resolver_id: selectedResolver?.resolver_id || val,
                                        resolver_attachment_id: selectedResolver?.resolver_attachment_id || null,
                                        resolver_type_id: selectedResolver?.resolver_type_id || selectedResolver?.resolver_id || null,
                                        input_mappings:
                                          current.input_mappings?.length
                                            ? current.input_mappings
                                            : buildDefaultInputMappings(selectedResolver, availableFields, builtinValues),
                                      }))
                                   }}
                                 >
                                   <SelectTrigger className="h-7 text-[10px] bg-background">
                                     <SelectValue />
                                   </SelectTrigger>
                                   <SelectContent>
                                     {resolvers.map(r => {
                                       const disabledReason = resolveResolverDisabledReason(r)
                                       return (
                                         <SelectItem
                                           key={resolveResolverOptionValue(r)}
                                           value={resolveResolverOptionValue(r)}
                                           disabled={Boolean(disabledReason)}
                                         >
                                           {resolveResolverTitle(r)}
                                         </SelectItem>
                                       )
                                     })}
                                     {resolvers.length === 0 ? (
                                       <SelectItem value="__empty__" disabled>
                                         No approved resolvers
                                       </SelectItem>
                                     ) : null}
                                   </SelectContent>
                                 </Select>
                              </div>
                              {(() => {
                                const selectedResolver = findResolverBySelection(resolvers, resolveStepResolverValue(step))
                                if (!selectedResolver) {
                                  return (
                                    <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 px-3 py-3 text-[10px] text-muted-foreground">
                                      No approved resolver is attached for this step yet.
                                    </div>
                                  )
                                }
                                const disabledReason = resolveResolverDisabledReason(selectedResolver)
                                const configuredMappings =
                                  step.input_mappings?.length
                                    ? step.input_mappings
                                    : buildDefaultInputMappings(selectedResolver, availableFields, builtinValues)
                                const previousResolveSteps = value.steps
                                  .slice(0, idx)
                                  .filter(
                                    (
                                      candidate,
                                    ): candidate is Extract<LayerExperiencePipelineStepDefinition, { kind: "resolve" }> =>
                                      candidate.kind === "resolve",
                                  )
                                return (
                                  <div className="rounded-lg border border-border/70 bg-muted/10 px-3 py-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-foreground">
                                        <Database className="h-3 w-3 text-primary" />
                                        {resolveResolverTitle(selectedResolver)}
                                      </div>
                                      <span
                                        className={cn(
                                          "rounded border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider",
                                          resolveResolverAvailabilityClassName(selectedResolver.availability),
                                        )}
                                      >
                                        {resolveResolverAvailabilityLabel(selectedResolver.availability)}
                                      </span>
                                      {selectedResolver.provider ? (
                                        <span className="rounded border border-border/70 bg-background px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                                          {selectedResolver.provider}
                                        </span>
                                      ) : null}
                                    </div>
                                    <p className="mt-2 text-[10px] text-muted-foreground">
                                      {selectedResolver.description || "Resolver metadata is available for this step."}
                                    </p>
                                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                                      <div className="rounded-md border border-border/60 bg-background px-2.5 py-2">
                                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
                                          Attachment
                                        </div>
                                        <div className="mt-1 break-all text-[10px] font-semibold text-foreground">
                                          {selectedResolver.resolver_attachment_id || "Managed by host"}
                                        </div>
                                      </div>
                                      <div className="rounded-md border border-border/60 bg-background px-2.5 py-2">
                                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
                                          Resolver Id
                                        </div>
                                        <div className="mt-1 break-all text-[10px] font-semibold text-foreground">
                                          {selectedResolver.resolver_type_id || selectedResolver.resolver_id}
                                        </div>
                                      </div>
                                    </div>
                                    {selectedResolver.required_inputs?.length ? (
                                      <div className="mt-3">
                                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
                                          Required Inputs
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                          {selectedResolver.required_inputs.map(input => (
                                            <span
                                              key={input.input_key}
                                              className="rounded bg-background px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground shadow-sm"
                                            >
                                              {input.label || input.input_key}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null}
                                    {configuredMappings.length > 0 ? (
                                      <div className="mt-3 grid gap-2">
                                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
                                          Input Bindings
                                        </div>
                                        {configuredMappings.map((mapping, mappingIdx) => (
                                          <div
                                            key={`${mapping.input_key}-${mappingIdx}`}
                                            className="rounded-md border border-border/60 bg-background px-2.5 py-2"
                                          >
                                            <div className="mb-2 flex items-center justify-between gap-2">
                                              <div className="text-[10px] font-bold text-foreground">
                                                {selectedResolver.required_inputs?.find(input => input.input_key === mapping.input_key)?.label ||
                                                  mapping.input_key}
                                              </div>
                                              <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                                                {mapping.required === false ? "Optional" : "Required"}
                                              </span>
                                            </div>
                                            <div className="grid gap-2 md:grid-cols-[10rem_minmax(0,1fr)]">
                                              <Select
                                                value={mapping.binding_kind}
                                                onValueChange={value => {
                                                  const nextBindingKind = value as LayerExperienceResolverInputBindingKind
                                                  handleUpdateResolveStep(idx, current => {
                                                    const currentMappings =
                                                      current.input_mappings?.length
                                                        ? [...current.input_mappings]
                                                        : [...configuredMappings]
                                                    currentMappings[mappingIdx] = {
                                                      ...currentMappings[mappingIdx],
                                                      binding_kind: nextBindingKind,
                                                      binding:
                                                        nextBindingKind === "constant"
                                                          ? { value: "" }
                                                          : nextBindingKind === "builtin_value"
                                                            ? { value_id: builtinValues[0]?.value_id ?? "" }
                                                            : nextBindingKind === "semantic_role"
                                                              ? {
                                                                  role: semanticRoleOptions[0]?.role ?? "",
                                                                  field: semanticRoleOptions[0]?.fieldId ?? "",
                                                                }
                                                              : nextBindingKind === "resolver_result"
                                                                ? {
                                                                    step_id: previousResolveSteps[0]?.step_id ?? "",
                                                                  }
                                                                : { field: availableFields[0]?.id ?? "" },
                                                    }
                                                    return {
                                                      ...current,
                                                      input_mappings: currentMappings,
                                                    }
                                                  })
                                                }}
                                              >
                                                <SelectTrigger className="h-8 bg-background text-[10px]">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="feature_field">Feature Field</SelectItem>
                                                  <SelectItem value="semantic_role">Semantic Role</SelectItem>
                                                  <SelectItem value="builtin_value">Built-In Value</SelectItem>
                                                  <SelectItem value="constant">Constant</SelectItem>
                                                  <SelectItem value="resolver_result">Previous Result</SelectItem>
                                                </SelectContent>
                                              </Select>
                                              {mapping.binding_kind === "feature_field" ? (
                                                <Select
                                                  value={typeof mapping.binding?.field === "string" ? mapping.binding.field : ""}
                                                  onValueChange={value => {
                                                    handleUpdateResolveStep(idx, current => {
                                                      const currentMappings =
                                                        current.input_mappings?.length
                                                          ? [...current.input_mappings]
                                                          : [...configuredMappings]
                                                      currentMappings[mappingIdx] = {
                                                        ...currentMappings[mappingIdx],
                                                        binding: { field: value },
                                                      }
                                                      return { ...current, input_mappings: currentMappings }
                                                    })
                                                  }}
                                                >
                                                  <SelectTrigger className="h-8 bg-background text-[10px]">
                                                    <SelectValue placeholder="Select a field" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {availableFields.map(field => (
                                                      <SelectItem key={field.id} value={field.id}>
                                                        {field.label}
                                                      </SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              ) : null}
                                              {mapping.binding_kind === "semantic_role" ? (
                                                <Select
                                                  value={typeof mapping.binding?.role === "string" ? mapping.binding.role : ""}
                                                  onValueChange={value => {
                                                    const selectedRole = semanticRoleOptions.find(option => option.role === value)
                                                    handleUpdateResolveStep(idx, current => {
                                                      const currentMappings =
                                                        current.input_mappings?.length
                                                          ? [...current.input_mappings]
                                                          : [...configuredMappings]
                                                      currentMappings[mappingIdx] = {
                                                        ...currentMappings[mappingIdx],
                                                        binding: {
                                                          role: value,
                                                          field: selectedRole?.fieldId ?? "",
                                                        },
                                                      }
                                                      return { ...current, input_mappings: currentMappings }
                                                    })
                                                  }}
                                                >
                                                  <SelectTrigger className="h-8 bg-background text-[10px]">
                                                    <SelectValue placeholder="Select a semantic role" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {semanticRoleOptions.map(option => (
                                                      <SelectItem key={option.role} value={option.role}>
                                                        {option.label}
                                                      </SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              ) : null}
                                              {mapping.binding_kind === "builtin_value" ? (
                                                <Select
                                                  value={typeof mapping.binding?.value_id === "string" ? mapping.binding.value_id : ""}
                                                  onValueChange={value => {
                                                    handleUpdateResolveStep(idx, current => {
                                                      const currentMappings =
                                                        current.input_mappings?.length
                                                          ? [...current.input_mappings]
                                                          : [...configuredMappings]
                                                      currentMappings[mappingIdx] = {
                                                        ...currentMappings[mappingIdx],
                                                        binding: { value_id: value },
                                                      }
                                                      return { ...current, input_mappings: currentMappings }
                                                    })
                                                  }}
                                                >
                                                  <SelectTrigger className="h-8 bg-background text-[10px]">
                                                    <SelectValue placeholder="Select a built-in value" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {builtinValues.map(entry => (
                                                      <SelectItem key={entry.value_id} value={entry.value_id}>
                                                        {entry.label}
                                                      </SelectItem>
                                                    ))}
                                                  </SelectContent>
                                                </Select>
                                              ) : null}
                                              {mapping.binding_kind === "constant" ? (
                                                <Input
                                                  value={typeof mapping.binding?.value === "string" ? mapping.binding.value : ""}
                                                  onChange={event => {
                                                    const nextValue = event.target.value
                                                    handleUpdateResolveStep(idx, current => {
                                                      const currentMappings =
                                                        current.input_mappings?.length
                                                          ? [...current.input_mappings]
                                                          : [...configuredMappings]
                                                      currentMappings[mappingIdx] = {
                                                        ...currentMappings[mappingIdx],
                                                        binding: { value: nextValue },
                                                      }
                                                      return { ...current, input_mappings: currentMappings }
                                                    })
                                                  }}
                                                  placeholder="Enter a constant value"
                                                  className="h-8 text-[10px] bg-background"
                                                />
                                              ) : null}
                                              {mapping.binding_kind === "resolver_result" ? (
                                                <Select
                                                  value={typeof mapping.binding?.step_id === "string" ? mapping.binding.step_id : ""}
                                                  onValueChange={value => {
                                                    handleUpdateResolveStep(idx, current => {
                                                      const currentMappings =
                                                        current.input_mappings?.length
                                                          ? [...current.input_mappings]
                                                          : [...configuredMappings]
                                                      currentMappings[mappingIdx] = {
                                                        ...currentMappings[mappingIdx],
                                                        binding: { step_id: value },
                                                      }
                                                      return { ...current, input_mappings: currentMappings }
                                                    })
                                                  }}
                                                >
                                                  <SelectTrigger className="h-8 bg-background text-[10px]">
                                                    <SelectValue placeholder="Select a previous resolver step" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {previousResolveSteps.map(previousStep => (
                                                      <SelectItem key={previousStep.step_id} value={previousStep.step_id}>
                                                        {previousStep.step_id}
                                                      </SelectItem>
                                                    ))}
                                                    {previousResolveSteps.length === 0 ? (
                                                      <SelectItem value="__none__" disabled>
                                                        No previous resolver steps
                                                      </SelectItem>
                                                    ) : null}
                                                  </SelectContent>
                                                </Select>
                                              ) : null}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : null}
                                    {disabledReason ? (
                                      <div className="mt-3 flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-2 text-[10px] text-amber-700">
                                        <AlertCircle className="h-3 w-3" />
                                        {disabledReason}
                                      </div>
                                    ) : null}
                                  </div>
                                )
                              })()}
                              <div className="grid gap-1">
                                 <Label className="text-[9px] uppercase text-muted-foreground/60">Failure Policy</Label>
                                 <Select 
                                   value={step.failure_policy || "continue_with_empty_result"} 
                                   onValueChange={(val: any) => {
                                      const steps = [...value.steps]
                                      steps[idx] = { ...step, failure_policy: val } as LayerExperiencePipelineStepDefinition
                                      handleUpdate({ steps })
                                   }}
                                 >
                                   <SelectTrigger className="h-7 text-[10px] bg-background">
                                     <SelectValue />
                                   </SelectTrigger>
                                   <SelectContent>
                                     <SelectItem value="skip_step">Skip Step</SelectItem>
                                     <SelectItem value="stop_pipeline">Stop Pipeline</SelectItem>
                                     <SelectItem value="continue_with_empty_result">Continue (Empty)</SelectItem>
                                     <SelectItem value="show_runtime_warning">Show Warning</SelectItem>
                                   </SelectContent>
                                 </Select>
                              </div>
                           </div>
                        )}
                        {/* Condition and TargetSet omitted for brevity in builder, assuming host handles complex logic sub-editors if needed, 
                            but providing enough for "full-height builder" feel */}
                     </div>
                  </div>
               </div>
             ))}
             {value.steps.length === 0 && (
               <div className="p-4 border border-dashed rounded-lg bg-muted/5 flex flex-col items-center justify-center text-center">
                  <Activity className="w-5 h-5 text-muted-foreground/20 mb-1" />
                  <p className="text-[10px] text-muted-foreground italic">No logic steps defined. Pipeline will execute effects directly.</p>
               </div>
             )}
          </div>
        </div>

        <Separator />

        {/* Effects */}
        <div className="grid gap-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <Label className="text-[11px] font-bold uppercase text-muted-foreground tracking-tight">Terminal Effects</Label>
              <p className="text-[10px] text-muted-foreground">Actions performed after logic resolution</p>
            </div>
            <button 
              onClick={handleAddEffect}
              className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline"
            >
              <Plus className="w-3 h-3" /> Add Effect
            </button>
          </div>

          <div className="space-y-3">
             {value.effects.map((effect, idx) => (
               <div key={idx} className="p-3 border rounded-lg bg-emerald-500/[0.02] border-emerald-500/10 group relative">
                  <button 
                    onClick={() => handleRemoveEffect(idx)}
                    className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground/40 hover:text-destructive"
                  >
                     <Trash2 className="w-3 h-3" />
                  </button>

                  <div className="grid gap-3">
                     <div className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-[10px] font-bold uppercase text-emerald-700">Effect #{idx + 1}</span>
                     </div>

                     <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1">
                           <Label className="text-[9px] uppercase text-muted-foreground/60">Kind</Label>
                           <Select 
                             value={effect.kind} 
                             onValueChange={(val: any) => {
                                const effects = [...value.effects]
                                effects[idx] = { ...effect, kind: val }
                                handleUpdate({ effects })
                             }}
                           >
                             <SelectTrigger className="h-7 text-[10px] bg-background">
                               <SelectValue />
                             </SelectTrigger>
                             <SelectContent>
                               <SelectItem value="open_surface">Open Surface</SelectItem>
                               <SelectItem value="close_surface">Close Surface</SelectItem>
                               <SelectItem value="highlight_set">Highlight Features</SelectItem>
                               <SelectItem value="fly_to_feature">Fly To</SelectItem>
                               <SelectItem value="open_route">Open Route</SelectItem>
                               <SelectItem value="open_external_url">Open External URL</SelectItem>
                               <SelectItem value="copy_field">Copy Field</SelectItem>
                               <SelectItem value="bind_surface_data">Bind Surface Data</SelectItem>
                             </SelectContent>
                           </Select>
                        </div>

                        {(effect.kind === "open_surface" || effect.kind === "close_surface") && (
                           <div className="grid gap-1">
                              <Label className="text-[9px] uppercase text-muted-foreground/60">Surface</Label>
                              <Select 
                                value={effect.surface_id || ""} 
                                onValueChange={(val) => {
                                   const effects = [...value.effects]
                                   effects[idx] = { ...effect, surface_id: val }
                                   handleUpdate({ effects })
                                }}
                              >
                                <SelectTrigger className="h-7 text-[10px] bg-background">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {invokableSurfaces.map(s => (
                                    <SelectItem key={s.surface_id} value={s.surface_id}>{s.label || s.surface_id}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                           </div>
                        )}

                        {(effect.kind === "open_route" || effect.kind === "open_external_url") && (
                           <div className="grid gap-1">
                              <Label className="text-[9px] uppercase text-muted-foreground/60">
                                {effect.kind === "open_external_url" ? "URL Template" : "Route Template"}
                              </Label>
                              <Input
                                value={resolveEffectTemplate(effect)}
                                onChange={(e) => {
                                  const effects = [...value.effects]
                                  effects[idx] = {
                                    ...effect,
                                    input: {
                                      ...(effect.input && typeof effect.input === "object" && !Array.isArray(effect.input)
                                        ? effect.input
                                        : {}),
                                      template: e.target.value,
                                    },
                                  }
                                  handleUpdate({ effects })
                                }}
                                placeholder={
                                  effect.kind === "open_external_url"
                                    ? "https://external.example.com/accounts/{{customer_id}}"
                                    : "/accounts/{{customer_id}}"
                                }
                                className="h-7 text-[10px] bg-background font-mono"
                              />
                           </div>
                        )}

                        {effect.kind === "copy_field" && (
                           <div className="grid gap-1">
                              <Label className="text-[9px] uppercase text-muted-foreground/60">Field</Label>
                              <Input
                                value={resolveEffectField(effect)}
                                onChange={(e) => {
                                  const effects = [...value.effects]
                                  effects[idx] = {
                                    ...effect,
                                    input: {
                                      ...(effect.input && typeof effect.input === "object" && !Array.isArray(effect.input)
                                        ? effect.input
                                        : {}),
                                      field: e.target.value,
                                    },
                                  }
                                  handleUpdate({ effects })
                                }}
                                placeholder="customer_id"
                                className="h-7 text-[10px] bg-background font-mono"
                              />
                           </div>
                        )}
                     </div>
                  </div>
               </div>
             ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t bg-muted/10 flex items-center justify-end gap-3">
        <button 
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
        <button 
          onClick={onSave}
          className="px-4 py-1.5 bg-primary text-primary-foreground rounded text-xs font-bold shadow-sm hover:bg-primary/90 transition-colors"
        >
          Save Pipeline
        </button>
      </div>
    </div>
  )
}

function StepKindIcon({ kind, className }: { kind: string; className?: string }) {
  switch (kind) {
    case "resolve": return <Database className={className} />
    case "target_set": return <Search className={className} />
    case "condition": return <AlertCircle className={className} />
    default: return <Settings2 className={className} />
  }
}
